import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { regions as regionsApi, websites as api } from "../../api/endpoints";
import ShareWebsiteModal from "../../components/ShareWebsiteModal.jsx";
import SiteFavicon from "../../components/SiteFavicon.jsx";

export default function Websites() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionOptions, setRegionOptions] = useState([]);
  const [form, setForm] = useState({ label: "", domain: "", region: "global" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The website currently being shared (owner-only modal). Holds the
  // full row so the modal can show the label without an extra fetch.
  const [sharingWebsite, setSharingWebsite] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.list();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    regionsApi
      .list()
      .then((r) => setRegionOptions(r.data))
      .catch(() => setRegionOptions([]));
  }, []);

  // Re-poll after a short delay so the async metadata detector's
  // favicon / niche updates appear without a manual refresh.
  useEffect(() => {
    if (items.length === 0) return undefined;
    const missing = items.some((w) => !w.favicon_url || !w.niche);
    if (!missing) return undefined;
    const id = setTimeout(reload, 4000);
    return () => clearTimeout(id);
  }, [items]);

  async function onCreate(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.create(form);
      setForm({ label: "", domain: "", region: form.region || "global" });
      await reload();
    } catch (err) {
      const data = err?.response?.data;
      setError(
        (Array.isArray(data?.domain) && data.domain[0]) ||
          data?.domain ||
          t("websites.couldNotSave"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id) {
    if (!confirm(t("websites.deleteConfirm"))) return;
    await api.remove(id);
    await reload();
  }

  function regionLabel(code) {
    if (!code) return "";
    // Prefer the localized translation; fall back to the server label.
    const localized = t(`regions.${code}`, { defaultValue: "" });
    if (localized) return localized;
    const opt = regionOptions.find((r) => r.value === code);
    return opt?.label || code;
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("websites.title")}</h1>
        <p className="text-sm text-slate-600">{t("websites.subtitle")}</p>
      </div>

      <form onSubmit={onCreate} className="card space-y-4">
        <h2 className="text-sm font-semibold text-slate-700">
          {t("websites.addTitle")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("websites.label")}
            </label>
            <input
              className="input mt-1"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              placeholder={t("websites.labelPlaceholder")}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {t("websites.domain")}
            </label>
            <input
              type="text"
              className="input mt-1"
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              required
              placeholder={t("websites.domainPlaceholder")}
              autoComplete="off"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700">
              {t("websites.region")}
            </label>
            <select
              className="input mt-1"
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
            >
              {regionOptions.length === 0 ? (
                <option value="global">{t("regions.global", "Global")}</option>
              ) : (
                regionOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {regionLabel(r.value)}
                  </option>
                ))
              )}
            </select>
            <p className="text-xs text-slate-500 mt-1">{t("websites.regionHint")}</p>
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}
        <button className="btn-primary" disabled={busy}>
          {busy ? t("websites.adding") : t("websites.add")}
        </button>
      </form>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("websites.colLabel")}</th>
              <th className="px-4 py-3 font-medium">{t("websites.colDomain")}</th>
              <th className="px-4 py-3 font-medium">{t("websites.colNiche")}</th>
              <th className="px-4 py-3 font-medium">{t("websites.colRegion")}</th>
              <th className="px-4 py-3 font-medium">{t("websites.colAccess")}</th>
              <th className="px-4 py-3 font-medium">{t("websites.colAdded")}</th>
              <th className="px-4 py-3 font-medium w-px">&nbsp;</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-slate-500">
                  {t("common.loading")}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-slate-500">
                  {t("websites.noneYet")}
                </td>
              </tr>
            ) : (
              items.map((w) => (
                <tr key={w.id}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <span className="inline-flex items-center gap-2">
                      <SiteFavicon
                        url={w.favicon_url}
                        domain={w.domain}
                        label={w.label}
                        size={18}
                      />
                      {w.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{w.domain}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {w.niche ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-800 ring-1 ring-brand-100 text-xs px-2 py-0.5">
                        {w.niche}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">{t("websites.detecting")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{regionLabel(w.region)}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={w.role} t={t} />
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(w.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {w.is_owner && (
                      <button
                        onClick={() => setSharingWebsite(w)}
                        className="text-sm text-brand-700 hover:underline mr-3"
                      >
                        {t("websites.share")}
                      </button>
                    )}
                    {w.is_owner && (
                      <button
                        onClick={() => onDelete(w.id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        {t("common.delete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {sharingWebsite && (
        <ShareWebsiteModal
          website={sharingWebsite}
          onClose={() => setSharingWebsite(null)}
        />
      )}
    </div>
  );
}

function RoleBadge({ role, t }) {
  // role: "owner" | "editor" | "read_only" | null
  if (!role) return <span className="text-xs text-slate-400">—</span>;
  const styles = {
    owner: "bg-brand-50 text-brand-800 ring-brand-100",
    editor: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    read_only: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={[
        "inline-flex items-center rounded-full text-xs px-2 py-0.5 ring-1",
        styles[role] || styles.read_only,
      ].join(" ")}
    >
      {t(`sharing.role_${role}`, role)}
    </span>
  );
}
